/**
 * Phase 7D — Procurement Metrics dashboard.
 *
 * Lists every new-flow Cost Center grouped by Department, with four
 * financial columns:
 *
 *   Budget Allocated · PO Raised · Invoice Value · Payouts
 *
 * The columns trace the procurement-to-payment lifecycle. Backend joins
 * through Project at query time (no schema denormalisation).
 *
 * Styling mirrors the CFO's source spreadsheet: dept header rows above their
 * CCs, subtotal row per dept, grand total at the bottom. Month picker filters
 * the date range for PO/Invoice/Payout activity. Budget Allocated is
 * point-in-time (CC.monthly_estimate at request time).
 *
 * Gated to EAF / L3 Finance.
 */

import {
  Fragment,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fetchGet } from "../../../service/api_client";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../context_providers/modal/loader";
import DashboardSection from "../components/dashboard_section";

interface CCRow {
  id: string;
  name: string;
  spoc_user_ids: string[];
  business_unit_id?: string;
  business_unit_name?: string;
  department_id: string;
  department_name: string;
  period_months: number;
  budget_allocated: number;
  po_raised: number;
  invoice_value: number;
  payouts: number;
}
interface DeptBlock {
  id: string;
  name: string;
  ccs: CCRow[];
  totals: {
    budget_allocated: number;
    po_raised: number;
    invoice_value: number;
    payouts: number;
  };
  cc_count: number;
}
interface ProcurementData {
  month: string;
  range: { from: string; to: string };
  dept_blocks: DeptBlock[];
  grand_total: {
    budget_allocated: number;
    po_raised: number;
    invoice_value: number;
    payouts: number;
  };
}

const formatINR = (n: number) =>
  "₹" +
  (Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
const formatShort = (n: number) => {
  const v = Math.abs(n);
  if (v >= 1e7) return "₹" + (n / 1e7).toFixed(2) + "Cr";
  if (v >= 1e5) return "₹" + (n / 1e5).toFixed(2) + "L";
  if (v >= 1e3) return "₹" + (n / 1e3).toFixed(1) + "K";
  return "₹" + Math.round(n);
};

const ProcurementDashboardScreen: FunctionComponent = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProcurementData | null>(null);

  const today = useMemo(() => new Date(), []);
  const currentMonthKey = `${today.getFullYear()}-${String(
    today.getMonth() + 1,
  ).padStart(2, "0")}`;
  const [month, setMonth] = useState(currentMonthKey);

  const load = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/dashboard/procurement?month=${month}`;
      const res = await fetchGet(url);
      if (res.success) setData(res.data);
      else showToast({ type: ToastType.error, text: res.error });
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [month]);

  if (loading && !data) return <LoadingWidget />;
  if (!data) return null;

  return (
    <div className="p-3 sm:p-5 flex flex-col gap-4">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-lg md:text-xl font-bold text-gray-900">
            Procurement Metrics
          </h1>
          <p className="text-xs text-gray-500">
            Cost Centers grouped by Department · Budget vs. lifecycle totals
            (PO &rarr; Invoice &rarr; Payout)
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-gray-500">
              Month
            </label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="border border-gray-200 rounded-md px-2 py-1 text-xs"
            />
          </div>
          <button
            onClick={load}
            className="bg-myPrimaryColor text-white rounded-md text-xs px-3 py-1.5"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-md shadow-sm border-l-4 border-l-myPrimaryColor p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">
            Budget Allocated
          </div>
          <div className="text-lg md:text-xl font-semibold text-gray-900 mt-0.5">
            {formatShort(data.grand_total.budget_allocated)}
          </div>
        </div>
        <div className="bg-white rounded-md shadow-sm border-l-4 border-l-sky-400 p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">
            PO Raised
          </div>
          <div className="text-lg md:text-xl font-semibold text-gray-900 mt-0.5">
            {formatShort(data.grand_total.po_raised)}
          </div>
        </div>
        <div className="bg-white rounded-md shadow-sm border-l-4 border-l-amber-500 p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">
            Invoice Value
          </div>
          <div className="text-lg md:text-xl font-semibold text-gray-900 mt-0.5">
            {formatShort(data.grand_total.invoice_value)}
          </div>
        </div>
        <div className="bg-white rounded-md shadow-sm border-l-4 border-l-emerald-500 p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">
            Payouts
          </div>
          <div className="text-lg md:text-xl font-semibold text-gray-900 mt-0.5">
            {formatShort(data.grand_total.payouts)}
          </div>
        </div>
      </div>

      <DashboardSection
        title="Cost Center breakdown"
        subtitle={`${data.dept_blocks.length} department(s) · ${data.range.from} to ${data.range.to}`}
      >
        <div className="overflow-x-auto rounded-md border border-gray-300">
          <table className="w-full text-xs" style={{ borderCollapse: "collapse" }}>
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2 font-semibold border border-gray-300">
                  Cost Center
                </th>
                <th className="text-left px-3 py-2 font-semibold border border-gray-300">
                  BU
                </th>
                <th className="text-right px-3 py-2 font-semibold border border-gray-300">
                  Budget Allocated
                </th>
                <th className="text-right px-3 py-2 font-semibold border border-gray-300">
                  PO Raised
                </th>
                <th className="text-right px-3 py-2 font-semibold border border-gray-300">
                  Invoice Value
                </th>
                <th className="text-right px-3 py-2 font-semibold border border-gray-300">
                  Payouts
                </th>
              </tr>
            </thead>
            <tbody>
              {data.dept_blocks.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-10 text-gray-400 italic border border-gray-300"
                  >
                    No Cost Centers configured under any Department yet.
                  </td>
                </tr>
              )}
              {data.dept_blocks.map((block) => (
                <Fragment key={block.id}>
                  {/* Dept header row */}
                  <tr className="bg-purple-50">
                    <td
                      colSpan={6}
                      className="px-3 py-1.5 font-semibold text-purple-900 border border-purple-200"
                    >
                      Dept &mdash; {block.name}
                      <span className="text-[10px] text-purple-700/80 font-normal ml-2">
                        ({block.cc_count} CC{block.cc_count === 1 ? "" : "s"})
                      </span>
                    </td>
                  </tr>
                  {/* CC rows */}
                  {block.ccs.map((cc) => (
                    <tr
                      key={cc.id}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-3 py-1.5 border border-gray-200">
                        <div className="font-medium text-gray-900">
                          {cc.name}
                        </div>
                        <div className="text-[10px] font-mono text-gray-400">
                          {cc.id}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-gray-600 border border-gray-200">
                        {cc.business_unit_name || "—"}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums border border-gray-200">
                        {formatINR(cc.budget_allocated)}
                      </td>
                      <td
                        className={`px-3 py-1.5 text-right tabular-nums border border-gray-200 ${
                          cc.po_raised > cc.budget_allocated
                            ? "text-rose-700 font-semibold"
                            : ""
                        }`}
                      >
                        {formatINR(cc.po_raised)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums border border-gray-200">
                        {formatINR(cc.invoice_value)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums border border-gray-200">
                        {formatINR(cc.payouts)}
                      </td>
                    </tr>
                  ))}
                  {/* Dept subtotal */}
                  <tr className="bg-purple-50/40 font-semibold">
                    <td
                      className="px-3 py-1.5 text-purple-900 border border-purple-200"
                      colSpan={2}
                    >
                      {block.name} Subtotal
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums border border-purple-200">
                      {formatINR(block.totals.budget_allocated)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums border border-purple-200">
                      {formatINR(block.totals.po_raised)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums border border-purple-200">
                      {formatINR(block.totals.invoice_value)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums border border-purple-200">
                      {formatINR(block.totals.payouts)}
                    </td>
                  </tr>
                </Fragment>
              ))}
              {/* Grand total */}
              <tr className="bg-cyan-700 text-white font-semibold">
                <td
                  className="px-3 py-2 border border-cyan-800"
                  colSpan={2}
                >
                  Grand Total
                </td>
                <td className="px-3 py-2 text-right tabular-nums border border-cyan-800">
                  {formatINR(data.grand_total.budget_allocated)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums border border-cyan-800">
                  {formatINR(data.grand_total.po_raised)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums border border-cyan-800">
                  {formatINR(data.grand_total.invoice_value)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums border border-cyan-800">
                  {formatINR(data.grand_total.payouts)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-3 text-[10px] text-gray-500">
          <b>Budget Allocated</b> = CC's current monthly estimate. <b>PO Raised</b>,{" "}
          <b>Invoice Value</b>, <b>Payouts</b> are scoped to the selected month.
        </div>
      </DashboardSection>

      {loading && <LoadingWidget />}
    </div>
  );
};

export default ProcurementDashboardScreen;
